/**
 * equipment_tracker — every vertical. The four units most in need of a look
 * (maintenance, then left on site, in use, reserved, yard), from the same
 * equipment read the band's Equip out counts — unchanged, and pinned by the
 * casing goldens.
 *
 * A company with any other vertical ALSO reads GET /dashboard/equipment-status
 * (api#1136) and draws one block per vertical below it (VerticalEquipmentBlock):
 * fleet, rental assets, downhole tools, instruments. Casing is not in that
 * route, so nothing here is drawn twice. A casing-only company never asks it.
 *
 * WITHOUT CASING THE /equipment BLOCK IS NOT DRAWN, AND NOT ASKED FOR. It is
 * casing's equipment register: on a trucking tenant it read "No equipment
 * yet." directly above "Fleet: Active 9" (Apex Hauling, 2026-10-20). Such a
 * company sees its own verticals' blocks only, and the header's Equipment link
 * (that register) goes with it.
 */
import EquipmentDeployment from '../../dashboard/EquipmentDeployment';
import { queryLoadFailure } from '../../../lib/load-failure';
import { WidgetPanel, WidgetPending } from '../WidgetPanel';
import { useEquipmentStatus, useOverviewEquipment } from '../overviewQueries';
import { useAuth } from '../../../contexts/AuthContext';
import { hasGlobalMoneyVertical } from '../globalMoney';
import { VerticalEquipmentBlock } from '../VerticalEquipmentBlock';
import { LoadFailureNotice } from '../../ui/LoadFailureNotice';
import { queryFailureError } from '../../../lib/load-failure';

const PRIORITY: Record<string, number> = { maintenance: 0, left_on_site: 1, in_use: 2, reserved: 3, available: 4 };

function OtherVerticals({ first = false }: { first?: boolean }) {
  const q = useEquipmentStatus(true);
  const failure = q.data === undefined ? queryLoadFailure(q) : null;
  if (failure) return <LoadFailureNotice error={queryFailureError(q)} kind={failure} subject="the other verticals' equipment" className="py-4" />;
  if (q.data === undefined) return <WidgetPending rows={2} />;
  if (q.data === null) return <LoadFailureNotice error={new Error('No equipment-status payload')} kind="unreachable" subject="the other verticals' equipment" className="py-4" />;
  if (q.data.verticals.length === 0) return null;
  return (
    <div data-testid="equipment-other-verticals" className={first ? 'space-y-5' : 'mt-5 space-y-5 border-t border-fieldiq-border pt-4'}>
      {q.data.verticals.map((v) => <VerticalEquipmentBlock key={v.vertical} v={v} />)}
    </div>
  );
}

export default function EquipmentTrackerWidget() {
  const { hasVertical } = useAuth();
  const others = hasGlobalMoneyVertical(hasVertical);
  if (!hasVertical('casing_running')) {
    return (
      <WidgetPanel widgetKey="equipment_tracker" eyebrow="Needs a look first" title="Equipment Tracker">
        <div className="p-4">{others && <OtherVerticals first />}</div>
      </WidgetPanel>
    );
  }
  return <CasingEquipmentTracker others={others} />;
}

function CasingEquipmentTracker({ others }: { others: boolean }) {
  const q = useOverviewEquipment();
  const failure = q.data === undefined ? queryLoadFailure(q) : null;
  const top = (q.data ?? [])
    .filter((e: any) => e.status !== 'retired')
    .sort((a: any, b: any) => (PRIORITY[a.status] ?? 9) - (PRIORITY[b.status] ?? 9))
    .slice(0, 4);

  return (
    <WidgetPanel widgetKey="equipment_tracker" eyebrow="Needs a look first" title="Equipment Tracker" link={{ label: 'Equipment', to: '/equipment' }}>
      <div className="p-4">
        {!failure && q.data === undefined ? <WidgetPending rows={2} /> : <EquipmentDeployment equipment={top} failure={failure} />}
        {others && <OtherVerticals />}
      </div>
    </WidgetPanel>
  );
}
